<template>
  <div>
    <div class='flex flex-wrap flex-col sm:flex-row justify-between sm:items-center gap-4 max-w-[80rem]'>
      <div>
        <h2 class='text-jananavy-900 font-medium'><slot name='title' /></h2>
        <p class='font-light text-sm'><slot name='description' /></p>
      </div>
      <div class='flex gap-2' v-if='props.showFilter'>
        <SelectInput :options='viewOptions' v-model='state.view' label='View' :srLabel='true' />
      </div>
    </div>
    <LoadingLayout :isLoading='isLoading' class='mt-8' :slownessThreshold='0'>
      <ReportNoDataWarning :warn='!view' :limitWidth='true'>
        <GraphStatCards :stats='view.stats' :isPrint='props.isPrint' />

        <div class='mt-8' :class='!props.isPrint ? "overflow-auto" : ""'>
          <table class='min-w-full 2xl:min-w-[80rem] table-fixed'>
            <thead class='bg-jananavy-900 text-white'>
              <tr>
                <th class='text-left font-medium px-4 py-2'>Objective</th>
                <th v-if='props.showOrganisation' class='text-left font-medium px-4 py-2'>Organisation</th>
                <th v-if='props.showAsset' class='text-left font-medium px-4 py-2'>Asset</th>
                <th v-if='props.showAssetManager' class='text-left font-medium px-4 py-2'>Asset Manager</th>
                <th class='text-left font-medium px-4 py-2' v-for='period in view.periods'>{{ period.header.name }}</th>
              </tr>
            </thead>
            <tbody class='divide-y border-b'>
              <template v-for='theme in view.themes'>
                <template v-if='state.view === "all" || state.view === theme.header.id'>
                  <tr>
                    <th class='px-4 py-2 text-left font-medium bg-janaturq-400 text-jananavy-900 text-sm sticky left-0'>{{ theme.header.name }}</th>
                    <th v-for='i in view.periods.length + props.showOrganisation + props.showAsset + props.showAssetManager' class='bg-janaturq-400'></th>
                  </tr>
                  <template v-for='objective in theme.objectives' :key='objective.header.id'>
                    <template v-for='entity, index in objective.entities' :key='entity.header.id'>
                      <tr>
                        <td class='px-4 py-2 text-sm font-light bg-janaturq-100 border-r border-t border-t-jananavy-900 sticky left-0' valign='top' v-if='!index' :rowspan='objective.entities.length'>
                          {{ objective.header.name }}
                        </td>
                        <td v-if='props.showOrganisation' class='whitespace-nowrap px-4 py-2 text-sm font-light bg-janaturq-100 sticky left-0' :class='[!index ? "border-t border-t-jananavy-900" : ""]'>
                          {{ entity.header.organisation.name }}
                        </td>
                        <td v-if='props.showAsset' class='whitespace-nowrap px-4 py-2 text-sm font-light bg-janaturq-100 sticky left-0' :class='[!index ? "border-t border-t-jananavy-900" : ""]'>
                          {{ entity.header.asset.name }}
                        </td>
                        <td v-if='props.showAssetManager' class='whitespace-nowrap px-4 py-2 text-sm font-light bg-janaturq-100 sticky left-0' :class='[!index ? "border-t border-t-jananavy-900" : ""]'>
                          {{ entity.header.assetManager.name }}
                        </td>
                        <td class='px-4 py-2 text-sm font-light' v-for='period in view.periods' :class='[!index ? "border-t border-t-jananavy-900" : ""]'>
                          <div class='flex items-center gap-1'>
                            <span class='rounded-sm w-6 h-6 inline-block' :class='scoreMap.getColour(entity.scores[period.key], "bg", "high")' />
                            <span>{{ scoreMap.getLabel(entity.scores[period.key]) }}</span>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </template>
                </template>
              </template>
            </tbody>
          </table>
        </div>

        <nav class="flex items-center justify-between bg-white mt-6 2xl:max-w-[80rem]" aria-label="Pagination">
          <div>
            <p class="text-sm text-gray-700">
              Page
              {{ ' ' }}
              <span class="font-medium">{{ request.getData()?.page }}</span>
              {{ ' ' }}
              of
              <span class="font-medium">{{ request.getData()?.totalPages }}</span>
            </p>
          </div>
          <div class="flex flex-1 justify-end">
            <button class="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0" @click='prevPage' v-if='request.getData()?.hasPrevPage'>Previous</button>
            <button class="relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0" @click='nextPage' v-if='request.getData()?.hasNextPage'>Next</button>
          </div>
        </nav>
      </ReportNoDataWarning>
    </LoadingLayout>
  </div>
</template>

<script setup>

import { computed, watch, reactive } from 'vue';

import SelectInput from '@/components/inputs/select-input.vue';

import LoadingLayout from '@/components/layouts/loading-layout.vue';

import ReportNoDataWarning from '@/components/report/report-no-data-warning.vue';

import GraphStatCards from '@/components/reports/graphs/graph-stat-cards.vue';

import { usePayloadRequest } from '@/kernel/api';
import { scoreMap } from '@/kernel/scoring';

const props = defineProps({
  params: Object,
  showOrganisation: Boolean,
  showAsset: Boolean,
  showAssetManager: Boolean,
  view: String,
  isPrint: Boolean,
  showFilter: Boolean
});

const state = reactive({
  view: 'all'
});

const viewOptions = computed(() => {
  const options = [
    {label: 'All Themes', value: 'all'}
  ];

  const data = request.getData();

  if (!data?.docs?.length) {
    return options;
  }

  const themes = {};

  data.docs.forEach((doc) => {
    const themeId = doc.theme.id;

    if (!themes[themeId]) {
      themes[themeId] = doc.theme;
    }
  });

  return [
    ...options,
    ...Object.values(themes).map(theme => ({label: theme.name, value: theme.id}))
  ];
});

const request = usePayloadRequest('GET', '/assessment-response/historical-matrix');

const isLoading = computed(() => {
  return request.isLoading() || request.getData() === null;
});

const view = computed(() => {
  const data = request.getData();

  if (!data?.docs?.length) {
    return null;
  }

  const themes = {};
  const periods = {};

  data.docs.forEach((doc) => {
    const themeId = doc.theme.id;
    const objectiveId = doc.objective.id;

    const entity = getEntity(doc);
    const entityId = entity.id;

    const period = {
      key: doc.halfYear ? `y${doc.year}h${doc.halfYear}` : doc.year,
      header: {
        name: doc.halfYear ? `Y${doc.year} H${doc.halfYear}` : doc.year
      }
    }

    if (!periods[period.key]) {
      periods[period.key] = period;
    }

    if (!themes[themeId]) {
      themes[themeId] = {
        header: doc.theme,
        objectives: {}
      }
    }

    const theme = themes[themeId];

    if (!theme.objectives[objectiveId]) {
      theme.objectives[objectiveId] = {
        header: doc.objective,
        entities: {}
      }
    }

    const objective = theme.objectives[objectiveId];

    if (!objective.entities[entityId]) {
      objective.entities[entityId] = {
        header: entity,
        scores: {}
      }
    }

    objective.entities[entityId].scores[period.key] = doc.score;
  });

  Object.values(themes).forEach((theme) => {
    theme.objectives = Object.values(theme.objectives);

    theme.objectives.forEach((objective) => {
      objective.entities = Object.values(objective.entities);
    });
  });

  const stats = [
    {name: 'Engagements', value: data.supplemental.stats.answeredEngagements},
    {name: 'Themes', value: data.supplemental.stats.themeCode},
    {name: 'Objectives', value: data.supplemental.stats.objectiveCode}
  ];

  if (props.view === 'platform') {
    if (props.showAsset) {
      stats.push({name: 'Assets', value: data.supplemental.stats.platformAsset});
    }

    if (props.showAssetManager) {
      stats.push({name: 'Asset Managers', value: data.supplemental.stats.platformAssetManager});
    }
  } else {
    if (props.showAsset) {
      stats.push({name: 'Assets', value: data.supplemental.stats.organisationAsset});
    }

    if (props.showAssetManager) {
      stats.push({name: 'Asset Managers', value: data.supplemental.stats.organisationAssetManager});
    }
  }

  return {
    themes: Object.values(themes),
    periods: Object.values(periods),
    stats
  }
});

const getEntity = (doc) => {
  if (props.view === 'organisation') {
    return {
      id: `${doc.organisationAsset.id}.${doc.organisationAssetManager?.id || 'null'}`,
      asset: doc.organisationAsset,
      assetManager: doc.organisationAssetManager,
      organisation: doc.organisation
    }
  }

  return {
    id: `${doc.platformAsset.id}.${doc.platformAssetManager?.id || 'null'}`,
    asset: doc.platformAsset,
    assetManager: doc.platformAssetManager,
    organisation: doc.organisation
  }
}

const prevPage = () => {
  request.doRequest({
    params: {
      ...props.params,
      page: request.getData()?.page - 1
    }
  });
}

const nextPage = () => {
  request.doRequest({
    params: {
      ...props.params,
      page: request.getData()?.page + 1
    }
  });
}

const load = () => {
  if (!props.params) {
    return;
  }

  request.doRequest({
    params: {
      ...props.params
    }
  });
}

watch(() => props.params, () => {
  load();
});

load();

</script>
